import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Badge, Space, Drawer, Switch } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  BankOutlined,
  FileDoneOutlined,
  BellOutlined,
  SearchOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CloseOutlined,
  SunOutlined,
  MoonOutlined,
  TeamOutlined,
  HomeOutlined,
  TagsOutlined,
  SettingOutlined,
  ContactsOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  BoxPlotOutlined,
  ColumnHeightOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ControlOutlined,
  PrinterOutlined,
  PictureOutlined,
  BarChartOutlined,
  ShopOutlined,
  FileSyncOutlined,
  SolutionOutlined,
  FileSearchOutlined,
  UploadOutlined,
  BgColorsOutlined,
  CarOutlined,
  WalletOutlined,
  ToolOutlined,
  FileTextOutlined as ServiceFileOutlined,
  MessageOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { filterMenuByRole } from '../utils/permissions';
import { RoleRouteGuard } from './RoleRouteGuard';
import { ConfigProvider } from 'antd';
import './Layout.css';

const { Header, Sider, Content } = Layout;

interface LayoutProps {
  children: React.ReactNode;
}

const LayoutComponent: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme, menuStyle } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const roleCodes = user?.roleCodes ?? [];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const MENU_ICON_COLORS = 12;

  type MenuItemWithColor =
    | { type: 'divider' }
    | {
        key: string;
        icon?: React.ReactNode;
        label?: React.ReactNode;
        children?: MenuItemWithColor[];
        className?: string;
        disabled?: boolean;
      };

  const addMenuIconColors = (items: MenuItemWithColor[], startIndex: number, sameColorAsParent = false): { items: MenuItemWithColor[]; nextIndex: number } => {
    let idx = startIndex;
    const result = items.map((item) => {
      if ('type' in item && item.type === 'divider') return item;
      const colorClass = `menu-icon-${(idx % MENU_ICON_COLORS) + 1}`;
      const withClass = { ...item, className: colorClass };
      if ('children' in withClass && Array.isArray(withClass.children)) {
        const { items: childItems } = addMenuIconColors(withClass.children, idx, true);
        if (!sameColorAsParent) idx += 1;
        return { ...withClass, children: childItems };
      }
      if (!sameColorAsParent) idx += 1;
      return withClass;
    });
    return { items: result, nextIndex: idx };
  };

  const menuItemsBase: MenuItemWithColor[] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'service-management',
      icon: <ToolOutlined />,
      label: 'Servis Yönetimi',
      children: [
        {
          key: '/service-customers',
          icon: <UserOutlined />,
          label: 'Müşteriler',
        },
        {
          key: '/service-orders',
          icon: <ServiceFileOutlined />,
          label: 'Servis Fişleri',
        },
        {
          key: '/ajanda',
          icon: <CalendarOutlined />,
          label: 'Ajanda',
        },
        {
          key: '/service-items',
          icon: <ToolOutlined />,
          label: 'Hizmet Kalemleri',
        },
        {
          key: '/external-service-items',
          icon: <ToolOutlined />,
          label: 'Dış Hizmet Kalemleri',
        },
        {
          key: '/complaint-items',
          icon: <ToolOutlined />,
          label: 'Şikayet Kalemleri',
        },
      
      ],
    },
    
    {
      key: 'sales-purchase',
      icon: <FileTextOutlined />,
      label: 'Satış & Alış İşlemleri',
      children: [
        {
          key: '/documents',
          icon: <FileTextOutlined />,
          label: 'Tüm Fişler',
        },
        {
          key: '/invoices',
          icon: <FileDoneOutlined />,
          label: 'Faturalar',
          disabled: true,
        },
      ],
    },
    {
      key: 'product-stock',
      icon: <BoxPlotOutlined />,
      label: 'Ürün & Stok',
      children: [
        {
          key: '/products',
          icon: <ShoppingOutlined />,
          label: 'Ürünler',
        },
        {
          key: '/productgallery',
          icon: <PictureOutlined />,
          label: 'Ürün Galeri',
        },
        {
          key: '/print-labels',
          icon: <PrinterOutlined />,
          label: 'Etiket Yazdırma',
        },
        {
          key: '/products/price-bulk-update',
          icon: <DollarOutlined />,
          label: 'Fiyat Güncelleme Sihirbazı',
        },
        {
          key: '/products/price-bulk-update-percent',
          icon: <DollarOutlined />,
          label: 'Fiyat Güncelleme Yüzdelik',
        },
        {
          key: '/stock',
          icon: <DatabaseOutlined />,
          label: 'Stok Raporları',
        },
        {
          key: '/product-brands',
          icon: <TagsOutlined />,
          label: 'Ürün Markaları',
        },
      ],
    },
    {
      key: 'payments',
      icon: <DollarOutlined />,
      label: 'Ödemeler',
      children: [
        {
          key: '/payment',
          icon: <DollarOutlined />,
          label: 'Tahsilat / Ödeme al',
        },
        {
          key: '/payment/report',
          icon: <FileSearchOutlined />,
          label: 'Ödemeler dökümü',
        },
      ],
    },
    {
      key: 'cash-expenses',
      icon: <DollarOutlined />,
      label: 'Gider & Masraf',
      children: [
         {
          key: '/expenses',
          icon: <DollarOutlined />,
          label: 'Giderler',
        },
        {
          key: '/expense-types',
          icon: <DollarOutlined />,
          label: 'Gider Kalemleri',
        },
      ],
    },

    {
      key: 'accounts-channels',
      icon: <ContactsOutlined />,
      label: 'Cari & Müşteriler',
      children: [
        {
          key: '/accounts',
          icon: <ContactsOutlined />,
          label: 'Cari Hesaplar',
        },
        {
          key: '/customerdept',
          icon: <UserOutlined />,
          label: 'Alacaklar (Müşteri)',
        },
        {
          key: '/supplier-payables',
          icon: <UserOutlined />,
          label: 'Borçlar (Tedarikçi)',
        },
      ],
    },
   
    {
      key: 'cash-management',
      icon: <WalletOutlined />,
      label: 'Muhasebe & Finans ',
      children: [
        {
          key: '/cash-registers',
          icon: <DollarOutlined />,
          label: 'Kasalar',
        },
        {
          key: '/bank-accounts',
          icon: <BankOutlined />,
          label: 'Banka Yönetimi',
        },
      
      ],
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: 'Raporlar',
      children: [
        {
          key: '/reports/profit',
          icon: <BarChartOutlined />,
          label: 'Kârlılık Raporu',
        },
        {
          key: '/reports/detailedprofit',
          icon: <BarChartOutlined />,
          label: 'Detaylı Kârlılık',
        },
        {
          key: '/reports/personelprofit',
          icon: <BarChartOutlined />,
          label: 'Personel Performans',
        },
        
      ],
    },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: 'Bildirimler',
    },
    {
      key: 'system-management',
      icon: <ControlOutlined />,
      label: 'Sistem Yönetimi',
      children: [
        {
          key: '/users',
          icon: <TeamOutlined />,
          label: 'Kullanıcılar & Yetkiler',
        },
        {
          key: '/import-stock',
          icon: <UploadOutlined />,
          label: 'İçe Aktar (Stok)',
        },
        {
          key: '/stock-settings',
          icon: <SettingOutlined />,
          label: 'Stok Ayarları',
        },
        {
          key: '/general-sms-settings',
          icon: <SettingOutlined />,
          label: 'Genel SMS Ayarları',
        },
        {
          key: '/vehicles',
          icon: <CarOutlined />,
          label: 'Araç Marka & Model ',
        },
        {
          type: 'divider',
        },
        {
          key: '/sms-log',
          icon: <MessageOutlined />,
          label: 'SMS Log',
        },
        {
          key: '/system/logs',
          icon: <BellOutlined />,
          label: 'Log Kayıtları',
          disabled: true,
        },
      ],
    },
  ];

  const menuItems = menuStyle === 'colored' ? addMenuIconColors(menuItemsBase, 0).items : menuItemsBase;

  const userMenuItems = [
 
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Çıkış',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout();
    }
  };

  const filteredMenuItems = filterMenuByRole(menuItems, roleCodes);

  const menuContent = (
    <Menu
      theme={theme}
      mode="inline"
      selectedKeys={[location.pathname]}
      items={filteredMenuItems as any}
      onClick={(e) => {
        handleMenuClick(e);
        if (isMobile) {
          setMobileMenuOpen(false);
        }
      }}
      className={`vuexy-menu${menuStyle === 'bw' ? ' menu-bw' : ''}`}
    />
  );

  return (
    <Layout className="vuexy-layout">
      {!isMobile && (
        <Sider 
          width={260} 
          theme={theme}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          className="vuexy-sider"
        >
          <div className="logo-wrapper">
            {!collapsed ? (
              <div className="logo-full">
                <span className="logo-icon"> <img src='../images/logo-s.png' style={{ width: '40px' }}></img>  </span>
                <span className="logo-text"> {process.env.REACT_APP_APP_TITLE ?? 'OTOERP v1.0'}</span>
              </div>
            ) : (
              <div className="logo-collapsed">
                <span className="logo-icon"> <img src='../images/logo-s.png' style={{ width: '40px' }}></img></span>
              </div>
            )}
          </div>
          {menuContent}
        </Sider>
      )}
      
      <Layout className="vuexy-content-wrapper">
        <Header className="vuexy-header">
          <div className="header-left">
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuUnfoldOutlined />}
                onClick={() => setMobileMenuOpen(true)}
                className="collapse-btn"
              />
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className="collapse-btn"
              />
            )}
            {/* {!isMobile && (
              <div className="search-wrapper">
                <SearchOutlined className="search-icon" />
                <input type="text" placeholder="Ara..." className="search-input" />
              </div>
            )} */}
          </div>
          <div className="header-right">
            <Space size="large">
              <Button
                type="text"
                icon={theme === 'light' ? <MoonOutlined /> : <SunOutlined />}
                onClick={toggleTheme}
                className="theme-toggle-btn"
                title={theme === 'light' ? 'Dark Mode' : 'Light Mode'}
              />
              {/* {!isMobile && (
                <Badge count={5} size="small">
                  <Button type="text" icon={<BellOutlined />} className="header-icon" />
                </Badge>
              )} */}
              <Dropdown
                menu={{
                  items: userMenuItems,
                  onClick: handleUserMenuClick,
                }}
                placement="bottomRight"
              >
                <Button type="text" className="user-dropdown">
                  <Avatar className="user-avatar" icon={<UserOutlined />} />
                  {!isMobile && !collapsed && (
                    <span className="user-name">{user?.firstName} {user?.lastName}</span>
                  )}
                </Button>
              </Dropdown>
            </Space>
          </div>
        </Header>
        <Content className="vuexy-content">
          <RoleRouteGuard>
            {children}
          </RoleRouteGuard>
        </Content>
      </Layout>

      {isMobile && (
        <Drawer
          title={
            <div className="logo-full">
              <span className="logo-icon"> 
              <img src='../images/logo-s.png' style={{ width: '40px' }}></img> 
                </span>
             
              <span className="logo-text"> OTO ERP  </span>
            </div>
          }
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          closable={true}
          closeIcon={<CloseOutlined />}
          width={260}
          className="mobile-drawer"
        >
          {menuContent}
        </Drawer>
      )}
    </Layout>
  );
};

export default LayoutComponent;
